import * as React from 'react';
import {Helmet} from 'react-helmet';

import type {FilterObject} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {ShowHideButton} from 'shells/button/show-hide-button/show-hide-button';
import {ButtonGroup} from 'shells/button-group/button-group';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {headTitle} from 'shells/document/head-title';
import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';
import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
// @ts-expect-error - imported from .js/.jsx file
import {EmptyFolderIcon} from 'shells/icon/svg/empty-folder-icon';
// @ts-expect-error - imported from .js/.jsx file
import {FolderIcon} from 'shells/icon/svg/folder-icon';
import {MainContentContainer} from 'shells/layout/main-content-container';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {EmailSequenceTemplateDuplicateInput} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';
import {PageTitleBar} from '../../../layout';

import {bustManagedCollectionCache} from '../../../../util/bust-managed-collection-cache';

import {useDeleteEmailSequenceTemplate} from '../../graphql/hooks';
import {useUpdateEmailSequenceTemplate} from '../../graphql/hooks/use-update-email-sequence-template';

import {EmailTemplateDesignerModal} from '../email-template-designer-modal';
import {getEmailSequenceTemplateFilters} from '../email-utils/get-email-sequence-template-filters';

import {ArchivedTableWrapper} from './archived-table-wrapper';
import {ChangeGroupModal} from './change-group-modal';
import {
    useGetEmailTemplateGroups,
    useCreateEmailSequenceTemplateGroup,
    useCreateEmailSequenceTemplate,
} from './graphql/hooks';
import {useDuplicateEmailSequenceTemplate} from './graphql/hooks/use-duplicate-email-sequence-template';
import {GroupTable} from './group-table';
import {GroupTableUncategorized} from './group-table-uncategorized';
import {TemplateGroupsEmailReportCta} from './template-groups-email-report-cta';
import {TemplateGroupsFilterSection} from './template-groups-filter-section';

type Props = {
    activeFilters: FilterObject[];
    onUpdateFilters: (arg1: FilterObject, arg2: string) => void;
    onParamChange: (arg1: any, arg2: boolean) => void;
    onRemoveAllFilters: () => void;
};

type OwnProps = Props & {
    groups: any[];
    refetchGroups: () => Promise<any>;
    uncategorized: any[];
    isLoading: boolean;
    errorMessage: string | null | undefined;
    onlyArchived: boolean;
    justMine: boolean;
    search: string;
    count: number;
    queryVariables: any;
    isFiltering: boolean;
    isJustMineEmpty: boolean;
    isCompletelyEmpty: boolean;
    networkStatus: number;
};

export function TemplateGroupsPageComponent(props: OwnProps) {
    const groupRefs = React.useRef([]);
    const [scrollToGroupId, setScrollToGroupId] = React.useState<string | null | undefined>(null);
    const [createGroupErrorMessage, setCreateGroupErrorMessage] = React.useState<
        string | null | undefined
    >(null);
    const [isShowingEmptyGroups, setIsShowingEmptyGroups] = React.useState<boolean>(false);
    // When a new group is created we want to always show it, even if empty.
    const [newlyCreatedGroupIds, setShownGroupIds] = React.useState<string[]>([]);

    const {refetchGroups, groups, uncategorized, onlyArchived, justMine, search} = props;

    React.useEffect(() => {
        refetchGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onlyArchived]);

    const {createEmailSequenceTemplateGroup, isCreating} = useCreateEmailSequenceTemplateGroup();
    const {createEmailSequenceTemplate} = useCreateEmailSequenceTemplate(); // will take param of groupId
    const {updateEmailSequenceTemplate, isUpdating} = useUpdateEmailSequenceTemplate();
    const {duplicateEmailSequenceTemplate, isLoading: isDuplicating} =
        useDuplicateEmailSequenceTemplate();

    // @ts-expect-error
    const onRenameEmailSequenceTemplate = (name) => {
        updateEmailSequenceTemplate({
            emailSequenceTemplate: {
                id: sequenceTemplateId || '',
                name: name,
            },
        }).then(() => {
            setSequenceTemplateName(null);
            setShouldShowRenameModal(false);
        });
    };

    const {user} = useGetSessionUser();
    const {deleteEmailSequenceTemplate} = useDeleteEmailSequenceTemplate();

    const [isCreateNewGroupModalOpen, setIsCreateNewGroupModalOpen] = React.useState(false);

    React.useEffect(() => {
        if (scrollToGroupId) {
            const refIndex = groups.findIndex((group) => group.id === scrollToGroupId);
            if (refIndex) {
                const ref = groupRefs.current[refIndex];
                if (ref) {
                    // @ts-expect-error
                    ref.scrollIntoView({behavior: 'instant'});
                    setScrollToGroupId(null);
                }
            }
        }
    }, [scrollToGroupId, groups]);

    const createNewGroupModal = () => {
        // @ts-expect-error
        const handleSave = (name) => {
            createEmailSequenceTemplateGroup({name})
                .then(({data}) => {
                    setIsCreateNewGroupModalOpen(false);
                    refetchGroups().then(() => {
                        const newGroupId = data
                            ? data.emailSequenceTemplateGroupCreate.emailSequenceTemplateGroup.id
                            : null;

                        if (newGroupId) {
                            setScrollToGroupId(newGroupId);
                            setShownGroupIds([...newlyCreatedGroupIds, newGroupId]);

                            bustManagedCollectionCache('emailSequenceTemplateGroups');
                        }
                    });
                })
                .catch((e) => {
                    setCreateGroupErrorMessage(safelyGetGraphQLErrorMessage(e));
                });
        };

        return (
            <SingleInputModal
                requireValue={true}
                buttonText='Create group'
                title='Create new template group'
                subtitle='Give your template group a descriptive name. All groups are public and visible to everyone in Nutshell'
                placeholder='Sales emails'
                isSaving={isCreating}
                onClose={() => {
                    setCreateGroupErrorMessage(null);
                    setIsCreateNewGroupModalOpen(false);
                }}
                onSave={handleSave}
                errorMessage={createGroupErrorMessage || ''}
            />
        );
    };

    const [shouldShowModal, setShouldShowModal] = React.useState(false);
    const [shouldShowRenameModal, setShouldShowRenameModal] = React.useState(false);
    const [shouldShowChangeGroupModal, setShouldShowChangeGroupModal] = React.useState(false);
    const [groupId, setGroupId] = React.useState<any>(null);
    const [sequenceTemplateId, setSequenceTemplateId] = React.useState<any>(null);
    const [sequenceTemplateName, setSequenceTemplateName] = React.useState<any>(null);

    const resetEmailTemplateDesignerModal = () => {
        setShouldShowModal(false);
        setSequenceTemplateId(null);
        setSequenceTemplateName(null);
        setGroupId(null);
    };

    const emailTemplateDesignerModal = (
        <EmailTemplateDesignerModal
            isOpen={shouldShowModal}
            sequenceTemplateId={sequenceTemplateId}
            sentEmailId={null}
            onClose={() => resetEmailTemplateDesignerModal()}
            onChildTemplateSaved={() => resetEmailTemplateDesignerModal()}
            groupId={groupId}
            refetchGroups={refetchGroups}
            createEmailSequenceTemplate={(input) =>
                createEmailSequenceTemplate(input).then(() => refetchGroups())
            }
        />
    );

    const handleDeleteTemplate = (id: string, queryVariables: any) =>
        deleteEmailSequenceTemplate(id, queryVariables);

    const handleDuplicateTemplate = (input: EmailSequenceTemplateDuplicateInput) => {
        return duplicateEmailSequenceTemplate(input).then(() => {
            refetchGroups();
        });
    };

    const isViewingArchived = props.onlyArchived;

    // this handles having three states: main tables, archived table, and empty state
    let specialState = null;
    if (isViewingArchived) {
        specialState = (
            // @ts-expect-error
            <ArchivedTableWrapper activeFilters={props.activeFilters} isLoading={props.isLoading} />
        );
    } else if (props.isLoading && groups.length === 0 && uncategorized.length === 0) {
        specialState = <LoadingPage />;
    } else if (props.isCompletelyEmpty && groups.length === 0 && uncategorized.length === 0) {
        specialState = (
            <EmptyStateLarge
                title='Convert more leads with email templates'
                detailTextBullets={[
                    'Templates save you time and help you stay on message',
                    'Create personalized templates for different types of leads',
                    'Automate follow-up personalized email sequences',
                ]}
                icon={
                    <img
                        alt='Configure email automations graphic'
                        style={{width: 300}}
                        src='/include/images/email-marketing/email_automations_sending_empty_state.png'
                    />
                }
                cta={
                    <Button
                        variant='primary'
                        size='xlarge'
                        onClick={() => setShouldShowModal(true)}
                    >
                        Create an email template
                    </Button>
                }
            />
        );
    } else if (props.isJustMineEmpty && groups.length === 0 && uncategorized.length === 0) {
        specialState = (
            <EmptyStateLarge
                title='Convert more leads with email templates'
                detailTextBullets={[
                    'Templates save you time and help you stay on message',
                    'Create personalized templates for different types of leads',
                    'Automate follow-up personalized email sequences',
                ]}
                icon={
                    <img
                        alt='Configure email automations graphic'
                        style={{width: 300}}
                        src='/include/images/email-marketing/email_automations_sending_empty_state.png'
                    />
                }
                cta={
                    <Button
                        variant='primary'
                        size='xlarge'
                        onClick={() => setShouldShowModal(true)}
                    >
                        Create your first email template
                    </Button>
                }
            />
        );
    } else if (props.isFiltering && groups.length === 0 && uncategorized.length === 0) {
        specialState = (
            <NothingFoundEmptyState
                title='We couldn’t find any matching templates'
                subtitle='Try a broader search'
            />
        );
    }

    const emptyGroupCount = groups.filter(
        (group) => !group.emailSequenceTemplates.length && !newlyCreatedGroupIds.includes(group.id)
    ).length;
    const [templateGroupId, setTemplateGroupId] = React.useState('null');

    const showHideButtonNoun = emptyGroupCount === 1 ? 'empty group' : 'empty groups';

    return (
        <>
            <Helmet>
                <title>{headTitle('Email templates')}</title>
            </Helmet>
            <MainContentContainer whiteBackground={true}>
                <PageTitleBar
                    title='Email templates'
                    subheaderPrimaryText='Create and share templates to save time and automate follow-up'
                    buttons={
                        <ButtonGroup>
                            {user && user.permissions.canManageEmailTemplates ? (
                                <Button
                                    variant='secondary'
                                    size='big'
                                    onClick={() => setIsCreateNewGroupModalOpen(true)}
                                >
                                    Create new group
                                </Button>
                            ) : null}
                            <Button
                                variant='primary'
                                size='big'
                                onClick={() => setShouldShowModal(true)}
                            >
                                Create new template
                            </Button>
                        </ButtonGroup>
                    }
                />
                <ListPageControlBar
                    archiveTab={{
                        isActive: onlyArchived,
                        onChange: () => {
                            props.onParamChange({filter: {onlyArchived: !onlyArchived}}, false);
                        },
                    }}
                    leftSection={
                        <TemplateGroupsFilterSection
                            activeFilters={props.activeFilters}
                            onUpdateFilters={props.onUpdateFilters}
                            onParamChange={props.onParamChange}
                            onlyArchived={onlyArchived}
                            justMine={justMine}
                            search={search}
                            count={props.count}
                            onRemoveAllFilters={props.onRemoveAllFilters}
                            isFiltering={props.isFiltering}
                        />
                    }
                />
                <div className='pad-16'>
                    {specialState ? (
                        specialState
                    ) : (
                        <>
                            {emptyGroupCount > 0 ? (
                                /* It's an ugly experience to show a bunch of hidden groups so we hide them by default */
                                <div className='mb-8 text-align-center'>
                                    <ShowHideButton
                                        showText={`Hide ${emptyGroupCount} ${showHideButtonNoun}`}
                                        hideText={`Show ${emptyGroupCount} ${showHideButtonNoun}`}
                                        isShown={isShowingEmptyGroups}
                                        onToggle={() =>
                                            setIsShowingEmptyGroups(!isShowingEmptyGroups)
                                        }
                                    />
                                </div>
                            ) : undefined}
                            <StackLegacy spacing={16}>
                                {user &&
                                user.permissions.canManageEmailTemplates &&
                                groups.length >= 2 ? (
                                    // @ts-expect-error
                                    <TemplateGroupsEmailReportCta numGroups={groups.length} />
                                ) : undefined}
                                {groups
                                    .filter(
                                        (group) =>
                                            isShowingEmptyGroups ||
                                            group.emailSequenceTemplates.length ||
                                            newlyCreatedGroupIds.includes(group.id)
                                    )
                                    .map((group, index) => (
                                        <GroupTable
                                            key={group.id}
                                            groupId={group.id}
                                            groupName={group.name}
                                            activeFilters={props.activeFilters}
                                            templates={group.emailSequenceTemplates}
                                            canArchive={group.canArchive}
                                            canRename={group.canRename}
                                            icon={FolderIcon}
                                            onAddClick={() => {
                                                setShouldShowModal(true);
                                                setGroupId(group.id);
                                            }}
                                            onDeleteTemplate={(templateId: string) =>
                                                handleDeleteTemplate(
                                                    templateId,
                                                    props.queryVariables
                                                )
                                            }
                                            isLoading={props.isLoading}
                                            getTableHeaderRef={(tableHeaderRef) => {
                                                // @ts-expect-error
                                                groupRefs.current[index] = tableHeaderRef;
                                            }}
                                            errorMessage={props.errorMessage}
                                            setSequenceTemplateId={setSequenceTemplateId}
                                            setSequenceTemplateName={setSequenceTemplateName}
                                            setShouldShowModal={setShouldShowModal}
                                            setShouldShowRenameModal={setShouldShowRenameModal}
                                            setShouldShowChangeGroupModal={
                                                setShouldShowChangeGroupModal
                                            }
                                            setTemplateGroupId={setTemplateGroupId}
                                            refetchGroups={refetchGroups}
                                            networkStatus={props.networkStatus}
                                            onDuplicateTemplate={handleDuplicateTemplate}
                                            isDuplicating={isDuplicating}
                                        />
                                    ))}
                                <GroupTableUncategorized
                                    key='uncategorized'
                                    templates={uncategorized}
                                    activeFilters={props.activeFilters}
                                    icon={EmptyFolderIcon}
                                    onAddClick={() => setShouldShowModal(true)}
                                    onDeleteTemplate={(templateId: string, queryVariables: any) =>
                                        handleDeleteTemplate(templateId, queryVariables)
                                    }
                                    isLoading={props.isLoading}
                                    errorMessage={props.errorMessage}
                                    setSequenceTemplateId={setSequenceTemplateId}
                                    setSequenceTemplateName={setSequenceTemplateName}
                                    setShouldShowModal={setShouldShowModal}
                                    setShouldShowRenameModal={setShouldShowRenameModal}
                                    setShouldShowChangeGroupModal={setShouldShowChangeGroupModal}
                                    setTemplateGroupId={setTemplateGroupId}
                                    onDuplicateTemplate={handleDuplicateTemplate}
                                    isDuplicating={isDuplicating}
                                />
                            </StackLegacy>
                        </>
                    )}
                </div>
            </MainContentContainer>
            {isCreateNewGroupModalOpen && createNewGroupModal()}
            {shouldShowModal && emailTemplateDesignerModal}
            {shouldShowRenameModal ? (
                <SingleInputModal
                    title='Rename email template'
                    subtitle='This is for you and your team.  People you email won’t see it.'
                    buttonText='Rename'
                    initialValue={sequenceTemplateName || ''}
                    isSaving={isUpdating}
                    onSave={onRenameEmailSequenceTemplate}
                    onClose={() => setShouldShowRenameModal(false)}
                />
            ) : null}
            {shouldShowChangeGroupModal ? (
                <ChangeGroupModal
                    groups={props.groups}
                    templateGroupId={templateGroupId}
                    onChange={(newValue) => {
                        setTemplateGroupId(newValue.id);
                    }}
                    onSave={() => {
                        return updateEmailSequenceTemplate({
                            emailSequenceTemplate: {
                                id: sequenceTemplateId || '',
                                groupId: templateGroupId === 'null' ? null : templateGroupId,
                            },
                        }).then(() => {
                            refetchGroups();
                        });
                    }}
                    onClose={() => setShouldShowChangeGroupModal(false)}
                />
            ) : null}
        </>
    );
}

export function TemplateGroupsPage(props: Props) {
    const {creatorIds, onlyArchived, includeShared, search, includeUnsharedFromOthers} =
        getEmailSequenceTemplateFilters(props.activeFilters);

    const queryVariables = {
        creatorIds,
        search,
        onlyArchived,
        includeShared,
        includeUnsharedFromOthers,
    } as const;

    const {groups, uncategorized, count, isLoading, errorMessage, refetch, networkStatus} =
        useGetEmailTemplateGroups(queryVariables);

    const isFiltering = Boolean(creatorIds.length > 0 || search);
    const isJustMineEmpty =
        !creatorIds.length && !onlyArchived && !search && count === 0 && groups.length === 0;
    const isCompletelyEmpty = isJustMineEmpty && includeShared;

    return (
        <TemplateGroupsPageComponent
            {...props}
            groups={groups}
            uncategorized={uncategorized}
            isLoading={isLoading}
            errorMessage={errorMessage}
            refetchGroups={refetch}
            // @ts-expect-error
            onlyArchived={onlyArchived}
            justMine={!includeShared}
            // @ts-expect-error
            search={search}
            count={count}
            isFiltering={isFiltering}
            isJustMineEmpty={isJustMineEmpty}
            // @ts-expect-error
            isCompletelyEmpty={isCompletelyEmpty}
            queryVariables={queryVariables}
            networkStatus={networkStatus}
        />
    );
}
